import { argon2id } from 'hash-wasm';
import CryptoJS from 'crypto-js';

/**
 * Fixed application-level salt for the client-side Argon2id key derivation.
 * Per-user salting happens server-side when the backend re-hashes the loginSecret.
 */
const LOGIN_SALT = 'skincair-login-v2';

/** Whether the current deployment uses Argon2id (true) or legacy SHA256 (false). */
export function isArgon2Auth(): boolean {
  return import.meta.env.VITE_APP_AUTH_METHOD === 'argon2';
}

/**
 * Derive a loginSecret from the user's plaintext password using Argon2id.
 *
 * Parameters match the Flutter CryptoService:
 *   - memory:      16 384 KiB  (16 MB)
 *   - parallelism:  1          (web Workers ≠ threads; keep it at 1)
 *   - iterations:   2
 *   - hashLength:  32 bytes
 *   - salt:        UTF-8 of "skincair-login-v2"
 *
 * Returns the 32-byte hash as a standard Base64 string (with padding),
 * identical to `base64Encode(secretKey.extractBytes())` in Dart.
 */
export async function computeLoginSecret(password: string): Promise<string> {
  const saltBytes = new TextEncoder().encode(LOGIN_SALT);

  const hashBytes = await argon2id({
    password,
    salt: saltBytes,
    parallelism: 1,
    iterations: 2,
    memorySize: 16384,  // KiB
    hashLength: 32,
    outputType: 'binary',
  });

  // Convert Uint8Array → standard Base64 (with padding)
  let binary = '';
  for (let i = 0; i < hashBytes.length; i++) {
    binary += String.fromCharCode(hashBytes[i]);
  }
  return btoa(binary);
}

/** Legacy SHA256 hash (Base64-encoded). */
function computeSha256Hash(password: string): string {
  return CryptoJS.SHA256(password).toString(CryptoJS.enc.Base64);
}

export interface HashedPassword {
  /** The field name to use in the POST body: "loginSecret" (argon2) or "password" (sha256). */
  field: 'loginSecret' | 'password';
  /** The hashed value (Base64). */
  value: string;
  /**
   * Login endpoint suffix: "/login/v2/web" or "/login/web". SKIN-599: the web logs in on the
   * /web routes, and the API stamps the session as a web one from the route taken; that is
   * what limits a dermatologist's web session to its correction work. The bare routes are
   * the app's.
   */
  loginEndpoint: string;
  /** Register endpoint suffix: "/register/v2" or "/register". */
  registerEndpoint: string;
}

/**
 * Hash a password using the method configured in VITE_APP_AUTH_METHOD.
 *
 * Returns everything the caller needs to build the request:
 * field name, hashed value, and the correct endpoint paths.
 */
// ---------------------------------------------------------------------------
// KEK wrapping / unwrapping (AES-256-GCM via Web Crypto)
// ---------------------------------------------------------------------------

const PBKDF2_ITERATIONS = 600_000;

/**
 * Derive a 32-byte Password-Derived Key (PDK) from a plaintext password and salt
 * using PBKDF2-HMAC-SHA256.
 */
export async function derivePasswordKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
  const enc = new TextEncoder();
  const baseKey = await crypto.subtle.importKey('raw', enc.encode(password), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

/** Unwrap a KEK that was wrapped with AES-256-GCM. Returns the raw 32-byte key. */
export async function unwrapKek(
  nonce: string,
  ciphertext: string,
  wrappingKey: CryptoKey,
): Promise<Uint8Array> {
  const nonceBytes = Uint8Array.from(atob(nonce), c => c.charCodeAt(0));
  const ctBytes = Uint8Array.from(atob(ciphertext), c => c.charCodeAt(0));
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: nonceBytes },
    wrappingKey,
    ctBytes,
  );
  return new Uint8Array(decrypted);
}

/** Wrap a raw KEK with AES-256-GCM. Returns { nonce, ciphertext } as Base64 strings. */
export async function wrapKek(
  rawKek: Uint8Array,
  wrappingKey: CryptoKey,
): Promise<{ nonce: string; ciphertext: string }> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    wrappingKey,
    rawKek,
  );
  return {
    nonce: btoa(String.fromCharCode(...iv)),
    ciphertext: btoa(String.fromCharCode(...new Uint8Array(encrypted))),
  };
}

const KEK_CATEGORIES = ['personal_data', 'images', 'consents'] as const;

/**
 * Generate 3 random KEKs (personal_data, images, consents), wrap each with a PDK
 * derived from the user's password. Returns the wrapped KEKs and the salt used
 * for PBKDF2 derivation (both needed by the server).
 */
export async function generateAndWrapKeks(password: string): Promise<{
  wrappedKeks: { category: string; nonce: string; ciphertext: string }[];
  keyDerivationSalt: string;
}> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const pdk = await derivePasswordKey(password, salt);
  const wrappedKeks: { category: string; nonce: string; ciphertext: string }[] = [];

  for (const category of KEK_CATEGORIES) {
    const rawKek = crypto.getRandomValues(new Uint8Array(32));
    const wrapped = await wrapKek(rawKek, pdk);
    wrappedKeks.push({ category, ...wrapped });
  }

  return {
    wrappedKeks,
    keyDerivationSalt: btoa(String.fromCharCode(...salt)),
  };
}

export async function hashPassword(password: string): Promise<HashedPassword> {
  if (isArgon2Auth()) {
    const value = await computeLoginSecret(password);
    return {
      field: 'loginSecret',
      value,
      loginEndpoint: '/login/v2/web',
      registerEndpoint: '/register/v2',
    };
  }

  return {
    field: 'password',
    value: computeSha256Hash(password),
    loginEndpoint: '/login/web',
    registerEndpoint: '/register',
  };
}
